import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Avatar,
  Chip,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Badge,
  Fade,
  Rating,
  Divider,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Fab,
  Zoom,
  Autocomplete,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Star as StarIcon,
  PhotoCamera as PhotoCameraIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  AttachMoney as MoneyIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Code as CodeIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  CreditCard as CreditCardIcon,
  Link as LinkIcon,
  GitHub as GitHubIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DynamicSEO from '../components/DynamicSEO';
import { generateProfileSEO } from '../lib/seoUtils';
import { getCurrentUserProfile, getProfileById, updateCurrentUserProfile, getProfileReviews, ProfileData, Review, UpdateProfileData } from '../lib/api/profile';
import { purchaseContactAccess, getUserCredits, hasContactAccess as checkContactAccess } from '../lib/credits';
import { supabase } from '../lib/supabaseClient';
import { canCreateVerificationRequest } from '../lib/api/verification';

const skillOptions = [
  'React', 'Vue.js', 'Angular', 'Node.js', 'Python', 'Java', 'C#', 'PHP',
  'WordPress', 'Shopify', 'Web Design', 'UI/UX Design', 'Graphic Design',
  'SEO', 'Digital Marketing', 'Content Writing', 'Translation',
  'Data Analysis', 'Machine Learning', 'Mobile Development', 'DevOps',
  'Project Management', 'Business Consulting', 'Accounting', 'Legal Services'
];

const technologyOptions = [
  'React', 'Vue.js', 'Angular', 'Next.js', 'Nuxt.js', 'Svelte', 'SvelteKit',
  'Node.js', 'Express', 'Fastify', 'NestJS', 'Python', 'Django', 'Flask',
  'FastAPI', 'Java', 'Spring Boot', 'C#', '.NET', 'ASP.NET Core',
  'PHP', 'Laravel', 'Symfony', 'CodeIgniter', 'WordPress', 'Drupal',
  'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Elasticsearch',
  'Docker', 'Kubernetes', 'AWS', 'Azure', 'Google Cloud',
  'TypeScript', 'JavaScript', 'HTML5', 'CSS3', 'Sass', 'Less',
  'Tailwind CSS', 'Bootstrap', 'Material-UI', 'Ant Design',
  'Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator',
  'Git', 'GitHub', 'GitLab', 'Bitbucket', 'Jira', 'Trello',
  'Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator'
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    skills: [] as string[],
    company: '',
    website_url: '',
    portfolio_url: '',
    hourly_rate: 0,
  });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [hasContactAccess, setHasContactAccess] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [canVerify, setCanVerify] = useState(false);
  const [experience, setExperience] = useState<Array<{
    id: string;
    company: string;
    position: string;
    startDate: string;
    endDate: string;
    description: string;
    current: boolean;
  }>>([]);
  const [education, setEducation] = useState<Array<{
    id: string;
    institution: string;
    degree: string;
    field: string;
    startDate: string;
    endDate: string;
    description: string;
  }>>([]);
  const [portfolio, setPortfolio] = useState<Array<{
    id: string;
    title: string;
    description: string;
    image_url: string | null;
    project_url: string | null;
    github_url: string | null;
    technologies: string[];
    completed_date: string;
    featured: boolean;
  }>>([]);

  // Load profile data on component mount
  useEffect(() => {
    loadProfileData();
  }, [id]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let profile: ProfileData | null = null;
      let currentUser: ProfileData | null = null;
      
      // Always load current user to determine role and permissions
      try {
        currentUser = await getCurrentUserProfile();
        setCurrentUserRole(currentUser?.role || null);
      } catch (err) {
        console.log('No current user logged in');
      }
      
      if (id) {
        // Viewing another user's profile
        profile = await getProfileById(id);
        setIsOwnProfile(false);
        
        // Check if viewing own profile by ID
        if (currentUser && currentUser.id === id) {
          setIsOwnProfile(true);
          profile = currentUser; // Use current user data for own profile
        }
      } else {
        // Viewing own profile
        profile = currentUser;
        setIsOwnProfile(true);
      }
      
      if (profile) {
        setProfileData(profile);
        setEditForm({
          full_name: profile.full_name || '',
          email: profile.email || '',
          phone: profile.phone || '',
          location: profile.location || '',
          bio: profile.bio || '',
          skills: profile.skills || [],
          company: profile.company || '',
          website_url: profile.website_url || '',
          portfolio_url: profile.portfolio_url || '',
          hourly_rate: profile.hourly_rate || 0,
        });

        // Check contact access and load user credits
        if (currentUser && !isOwnProfile) {
          try {
            // Check if user already has contact access
            console.log('Checking contact access for:', currentUser.id, 'to provider:', profile.id);
            const access = await checkContactAccess(currentUser.id, profile.id);
            console.log('Contact access result:', access);
            setHasContactAccess(access);
            
            // Load user credits
            const credits = await getUserCredits(currentUser.id);
            console.log('User credits:', credits);
            setUserCredits(credits);
          } catch (err) {
            console.error('Error loading credits or checking access:', err);
            setHasContactAccess(false);
            setUserCredits(0);
          }
        } else {
          setHasContactAccess(false);
          setUserCredits(0);
        }
        
        // Load reviews for this profile
        try {
          const profileReviews = await getProfileReviews(profile.id);
          setReviews(profileReviews);
        } catch (err) {
          console.error('Error loading reviews:', err);
          setReviews([]);
        }
        
        // Load experience, education and portfolio data
        setExperience(profile.experience || []);
        setEducation(profile.education || []);
        setPortfolio(profile.portfolio || []);
        
        // Check verification eligibility for own profile - only for freelancers
        if (isOwnProfile && profile.role === 'FREELANCER') {
          try {
            const { canCreate } = await canCreateVerificationRequest();
            setCanVerify(canCreate);
          } catch (err) {
            console.error('Error checking verification eligibility:', err);
            setCanVerify(false);
          }
        } else {
          setCanVerify(false);
        }
      } else {
        setError(t('profile.profileNotFound'));
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(t('profile.errorLoadingProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditModeToggle = () => {
    if (isOwnProfile) {
      setIsEditMode(!isEditMode);
      if (!isEditMode && profileData) {
      setEditForm({
        full_name: profileData.full_name || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        location: profileData.location || '',
        bio: profileData.bio || '',
        skills: profileData.skills || [],
        company: profileData.company || '',
        website_url: profileData.website_url || '',
        portfolio_url: profileData.portfolio_url || '',
        hourly_rate: profileData.hourly_rate || 0,
      });
      }
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const updateData: UpdateProfileData = {
        ...editForm,
        experience: experience,
        education: education,
        portfolio: portfolio,
      };

      const updatedProfile = await updateCurrentUserProfile(updateData);
      setProfileData(updatedProfile);
      setIsEditMode(false);
      setSuccessMessage(t('profile.profileUpdatedSuccess'));
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(t('profile.errorSavingProfile'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setAvatarPreview(null);
    if (profileData) {
      setEditForm({
        full_name: profileData.full_name || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        location: profileData.location || '',
        bio: profileData.bio || '',
        skills: profileData.skills || [],
        company: profileData.company || '',
        website_url: profileData.website_url || '',
        portfolio_url: profileData.portfolio_url || '',
        hourly_rate: profileData.hourly_rate || 0,
      });
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'FREELANCER':
        return 'Фриленсър';
      case 'AGENCY':
        return 'Агенция';
      case 'CLIENT':
        return 'Клиент';
      case 'ADMIN':
        return 'Администратор';
      default:
        return 'Професионалист';
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSkillRemove = (skillToRemove: string) => {
    setEditForm(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleAddExperience = () => {
    const newExperience = {
      id: Date.now().toString(),
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      description: '',
      current: false,
    };
    setExperience(prev => [...prev, newExperience]);
  };

  const handleRemoveExperience = (id: string) => {
    setExperience(prev => prev.filter(exp => exp.id !== id));
  };

  const handleUpdateExperience = (id: string, field: string, value: any) => {
    setExperience(prev => prev.map(exp => 
      exp.id === id ? { ...exp, [field]: value } : exp
    ));
  };

  const handleAddEducation = () => {
    const newEducation = {
      id: Date.now().toString(),
      institution: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
      description: '',
    };
    setEducation(prev => [...prev, newEducation]);
  };

  const handleRemoveEducation = (id: string) => {
    setEducation(prev => prev.filter(edu => edu.id !== id));
  };

  const handleUpdateEducation = (id: string, field: string, value: any) => {
    setEducation(prev => prev.map(edu => 
      edu.id === id ? { ...edu, [field]: value } : edu
    ));
  };

  const handleAddPortfolio = () => {
    const newPortfolio = {
      id: Date.now().toString(),
      title: '',
      description: '',
      image_url: null,
      project_url: '',
      github_url: '',
      technologies: [],
      completed_date: '',
      featured: false,
    };
    setPortfolio(prev => [...prev, newPortfolio]);
  };

  const handleRemovePortfolio = (id: string) => {
    setPortfolio(prev => prev.filter(item => item.id !== id));
  };

  const handleUpdatePortfolio = (id: string, field: string, value: any) => {
    setPortfolio(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handlePortfolioImageUpload = async (id: string, file: File) => {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Моля изберете валиден файл с изображение');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Файлът е твърде голям. Максималният размер е 2MB');
        return;
      }

      const { convertFileToBase64 } = await import('../lib/api/profile');
      const base64String = await convertFileToBase64(file);
      handleUpdatePortfolio(id, 'image_url', base64String);
    } catch (error) {
      console.error('Error converting image to base64:', error);
      setError('Грешка при обработка на изображение');
    }
  };

  const handleBack = () => {
    if (id) {
      navigate('/find-talent');
    } else {
      navigate('/');
    }
  };

  const handleVerification = () => {
    navigate('/verification');
  };

  const handleContactAccess = async () => {
    if (!profileData) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const result = await purchaseContactAccess(profileData.id);
      
      if (result.success) {
        setHasContactAccess(true);
        setSuccessMessage(result.message);
        
        // Refresh user credits
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const credits = await getUserCredits(user.id);
          setUserCredits(credits);
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error purchasing contact access:', err);
      setError('Грешка при закупуване на достъп до контактните данни');
    } finally {
      setSaving(false);
    }
  };


  const processFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('profile.invalidFileType'));
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError(t('profile.fileTooLarge'));
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };


  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', position: 'relative', zIndex: 1 }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  // Error state
  if (error && !profileData) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
              color: '#ffffff',
            }}
          >
            Назад
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  // No profile found
  if (!profileData) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
{t('profile.profileNotFoundMessage')}
          </Alert>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
              color: '#ffffff',
            }}
          >
            Назад
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#0f0f23', 
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {profileData && (
        <DynamicSEO 
          seoData={generateProfileSEO(profileData, i18n.language)} 
        />
      )}
      {/* Animated floating shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
          borderRadius: '50%',
          opacity: 0.3,
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-20px) rotate(180deg)',
            },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: 40,
          height: 40,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
          borderRadius: '20%',
          opacity: 0.4,
          animation: 'float 8s ease-in-out infinite reverse',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-30px) rotate(-180deg)',
            },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '20%',
          width: 80,
          height: 80,
          background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
          borderRadius: '30%',
          opacity: 0.2,
          animation: 'float 10s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-25px) rotate(90deg)',
            },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '25%',
          width: 50,
          height: 50,
          background: 'linear-gradient(135deg, #10b981 0%, #28A0F0 100%)',
          borderRadius: '50%',
          opacity: 0.3,
          animation: 'float 7s ease-in-out infinite reverse',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-15px) rotate(-90deg)',
            },
          },
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '5%',
          width: 35,
          height: 35,
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: '50%',
          opacity: 0.25,
          animation: 'float 9s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-18px) rotate(180deg)',
            },
          },
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '8%',
          width: 45,
          height: 45,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #10b981 100%)',
          borderRadius: '25%',
          opacity: 0.2,
          animation: 'float 11s ease-in-out infinite reverse',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-35px) rotate(-180deg)',
            },
          },
        }}
      />

      <Header />
      
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                py: { xs: 1, sm: 1.5 },
                px: { xs: 2, sm: 3 },
                '&:hover': {
                  borderColor: '#28A0F0',
                  backgroundColor: 'rgba(40, 160, 240, 0.1)',
                },
              }}
            >
              Назад
            </Button>
            
            {isOwnProfile && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEditMode}
                      onChange={handleEditModeToggle}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#28A0F0',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#28A0F0',
                        },
                        '& .MuiSwitch-switchBase': {
                          color: '#a0a0a0',
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: 'rgba(160, 160, 160, 0.3)',
                        },
                        '& .MuiSwitch-switchBase:hover': {
                          backgroundColor: 'rgba(160, 160, 160, 0.1)',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      color: '#ffffff', 
                      fontWeight: 600,
                      fontSize: { xs: '0.8rem', sm: '0.9rem' }
                    }}>
                      {isEditMode ? 'Редактиране' : 'Преглед'}
                    </Typography>
                  }
                />
              </Box>
            )}
          </Box>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 4 }}>
          {/* Left Sidebar - Profile Info */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Profile Card */}
              <Fade in={true} timeout={800}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 4,
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                    {/* Avatar Section */}
                    <Box sx={{ position: 'relative', display: 'inline-block', mb: { xs: 2, sm: 3 } }}>
                      <Avatar
                        src={avatarPreview || profileData.avatar_url || '/images/default-avatar.png'}
                        sx={{
                          width: { xs: 100, sm: 140 },
                          height: { xs: 100, sm: 140 },
                          border: '4px solid rgba(40, 160, 240, 0.4)',
                          boxShadow: '0 0 20px rgba(40, 160, 240, 0.3)',
                        }}
                      />
                      {profileData.verified && (
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <CheckCircleIcon 
                              sx={{ 
                                color: '#10b981', 
                                fontSize: '1.5rem',
                                backgroundColor: '#0a0a0a',
                                borderRadius: '50%',
                                p: 0.5
                              }} 
                            />
                          }
                        />
                      )}
                      
                      {/* Avatar Upload for Edit Mode */}
                      {isEditMode && isOwnProfile && (
                        <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="avatar-upload"
                            type="file"
                            onChange={handleAvatarChange}
                          />
                          <label htmlFor="avatar-upload">
                            <IconButton
                              component="span"
                              sx={{
                                backgroundColor: 'rgba(40, 160, 240, 0.9)',
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: 'rgba(40, 160, 240, 1)',
                                },
                              }}
                            >
                              <PhotoCameraIcon />
                            </IconButton>
                          </label>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Name - Editable in edit mode */}
                    {isEditMode && isOwnProfile ? (
                      <TextField
                        fullWidth
                        value={editForm.full_name}
                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                        placeholder={t('profileEdit.placeholders.fullName')}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: '#28A0F0' },
                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                          },
                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                        }}
                      />
                    ) : (
                    <Typography variant="h4" sx={{ 
                      color: '#ffffff', 
                      fontWeight: 700, 
                      mb: 1,
                      fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                      background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      {profileData.full_name || 'Без име'}
                    </Typography>
                    )}
                    
                    {/* Role - Display only */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      {profileData.role === 'FREELANCER' ? (
                        <PsychologyIcon sx={{ color: '#28A0F0', mr: 1 }} />
                      ) : (
                        <BusinessIcon sx={{ color: '#28A0F0', mr: 1 }} />
                      )}
                      <Typography variant="h6" sx={{ color: '#28A0F0', fontWeight: 500 }}>
                        {getRoleText(profileData.role)}
                      </Typography>
                    </Box>

                    {/* Location - Editable in edit mode */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                      <LocationIcon sx={{ color: '#a0a0a0', mr: 1, fontSize: '1.2rem' }} />
                      {isEditMode && isOwnProfile ? (
                        <TextField
                          value={editForm.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          placeholder={t('profileEdit.placeholders.location')}
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#ffffff',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                              '&:hover fieldset': { borderColor: '#28A0F0' },
                              '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                            },
                            '& .MuiInputLabel-root': { color: '#a0a0a0' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                          }}
                        />
                      ) : (
                      <Typography variant="body1" sx={{ color: '#a0a0a0' }}>
                        {profileData.location || 'Локация не е посочена'}
                      </Typography>
                      )}
                    </Box>

                    {/* Rating */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                      <Rating
                        value={profileData.rating_avg || 0}
                        precision={0.1}
                        readOnly
                        sx={{
                          '& .MuiRating-iconFilled': {
                            color: '#fbbf24',
                          },
                          '& .MuiRating-iconEmpty': {
                            color: '#374151',
                          },
                        }}
                      />
                      <Typography variant="body1" sx={{ color: '#a0a0a0', ml: 1 }}>
                        {profileData.rating_avg || 0} ({profileData.rating_cnt || 0} отзива)
                      </Typography>
                    </Box>

                    {/* Stats */}
                    <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: { xs: 1.5, sm: 2 }, 
                          backgroundColor: 'rgba(40, 160, 240, 0.1)', 
                          borderRadius: 2 
                        }}>
                          <Typography variant="h5" sx={{ 
                            color: '#28A0F0', 
                            fontWeight: 700,
                            fontSize: { xs: '1.2rem', sm: '1.5rem' }
                          }}>
                            {profileData.completed_jobs || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#a0a0a0', 
                            fontSize: { xs: '0.7rem', sm: '0.8rem' }
                          }}>
                            Проекта
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: { xs: 1.5, sm: 2 }, 
                          backgroundColor: 'rgba(40, 160, 240, 0.1)', 
                          borderRadius: 2 
                        }}>
                          {isEditMode && isOwnProfile ? (
                            <TextField
                              value={editForm.hourly_rate}
                              onChange={(e) => handleInputChange('hourly_rate', parseFloat(e.target.value) || 0)}
                              type="number"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  color: '#28A0F0',
                                  fontWeight: 700,
                                  '& fieldset': { borderColor: 'rgba(40, 160, 240, 0.3)' },
                                  '&:hover fieldset': { borderColor: '#28A0F0' },
                                  '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                                },
                                '& input': { 
                                  textAlign: 'center', 
                                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                                },
                              }}
                            />
                          ) : (
                          <Typography variant="h5" sx={{ 
                            color: '#28A0F0', 
                            fontWeight: 700,
                            fontSize: { xs: '1.2rem', sm: '1.5rem' }
                          }}>
                            €{profileData.hourly_rate || 0}
                          </Typography>
                          )}
                          <Typography variant="body2" sx={{ 
                            color: '#a0a0a0', 
                            fontSize: { xs: '0.7rem', sm: '0.8rem' }
                          }}>
                            На час
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Verification Status */}
                    <Box sx={{ mb: 3 }}>
                      {/* For agencies, show as verified if they are approved */}
                      {profileData.role === 'AGENCY' ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={t('profileEdit.verification.verifiedAgency')}
                          sx={{
                            backgroundColor: 'rgba(16, 185, 129, 0.2)',
                            color: '#10b981',
                            border: '1px solid #10b981',
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <>
                          <Chip
                            icon={profileData.verified ? <CheckCircleIcon /> : <TimeIcon />}
                            label={profileData.verified ? 'Верифициран' : 'Неверифициран'}
                            sx={{
                              backgroundColor: profileData.verified ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                              color: profileData.verified ? '#10b981' : '#f59e0b',
                              border: `1px solid ${profileData.verified ? '#10b981' : '#f59e0b'}`,
                              fontWeight: 600,
                            }}
                          />
                          
                          {/* Verification status message for freelancers */}
                          {isOwnProfile && !profileData.verified && !canVerify && profileData.role === 'FREELANCER' && (
                            <Typography variant="body2" sx={{ 
                              color: '#a0a0a0', 
                              mt: 2, 
                              textAlign: 'center',
                              fontStyle: 'italic',
                              fontSize: '0.8rem'
                            }}>
                              Имате активна заявка за верификация
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>


                    {/* Contact Information - Only visible with access */}
                    {!isOwnProfile && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 2, fontWeight: 600 }}>
                          Контактни данни:
                      </Typography>
                        {hasContactAccess ? (
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                Email
                          </Typography>
                              <Typography variant="body2" sx={{ color: '#28A0F0' }}>
                                {profileData.email || 'Не е посочен'}
                              </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                Телефон
                          </Typography>
                              <Typography variant="body2" sx={{ color: '#28A0F0' }}>
                                {profileData.phone || 'Не е посочен'}
                              </Typography>
                        </Box>
                      </Stack>
                        ) : (
                          <Box sx={{ 
                            background: 'rgba(245, 158, 11, 0.1)', 
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center'
                          }}>
                            <Typography variant="body2" sx={{ color: '#f59e0b', mb: 1 }}>
                              За да видите контактните данни
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 1, fontSize: '0.8rem' }}>
                              Ваши кредити: {userCredits}
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<CreditCardIcon />}
                              onClick={handleContactAccess}
                              disabled={userCredits < 3 || saving}
                              sx={{ 
                                background: userCredits >= 3 
                                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                  : 'rgba(160, 160, 160, 0.3)',
                                color: '#ffffff',
                                '&:hover': { 
                                  background: userCredits >= 3 
                                    ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                                    : 'rgba(160, 160, 160, 0.3)',
                                },
                                '&:disabled': {
                                  color: 'rgba(255, 255, 255, 0.5)',
                                }
                              }}
                            >
                              {saving ? 'Закупуване...' : `Купи с кредити (3 кредита)`}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}


                    {/* Action Buttons */}
                    <Stack spacing={{ xs: 1.5, sm: 2 }}>
                      {isEditMode && isOwnProfile ? (
                        <>
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            sx={{ 
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: '#ffffff',
                              py: { xs: 1.2, sm: 1.5 },
                              px: { xs: 2, sm: 3 },
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              fontWeight: 600,
                              '&:hover': { 
                                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            {saving ? 'Запазване...' : 'Запази промените'}
                          </Button>
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                            sx={{ 
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                              color: '#ffffff',
                              py: { xs: 1.2, sm: 1.5 },
                              px: { xs: 2, sm: 3 },
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              '&:hover': { 
                                borderColor: '#ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              },
                            }}
                          >
                            Отказ
                          </Button>
                        </>
                      ) : isOwnProfile ? (
                        <>
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<EditIcon />}
                            onClick={handleEditModeToggle}
                            sx={{
                              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                              color: '#ffffff',
                              py: { xs: 1.2, sm: 1.5 },
                              px: { xs: 2, sm: 3 },
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              fontWeight: 600,
                              '&:hover': {
                                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            Редактирай профил
                          </Button>
                          
                          {/* Verification Button - only for freelancers */}
                          {!profileData.verified && profileData.role === 'FREELANCER' && (
                            <Button
                              variant="outlined"
                              fullWidth
                              startIcon={<VerifiedUserIcon />}
                              onClick={handleVerification}
                              disabled={!canVerify}
                              sx={{
                                borderColor: canVerify ? 'rgba(16, 185, 129, 0.5)' : 'rgba(160, 160, 160, 0.3)',
                                color: canVerify ? '#10b981' : '#a0a0a0',
                                py: { xs: 1.2, sm: 1.5 },
                                px: { xs: 2, sm: 3 },
                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                fontWeight: 600,
                                '&:hover': canVerify ? {
                                  borderColor: '#10b981',
                                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                  transform: 'translateY(-1px)',
                                } : {},
                                '&:disabled': {
                                  color: '#a0a0a0',
                                  borderColor: 'rgba(160, 160, 160, 0.3)',
                                }
                              }}
                            >
                              {canVerify ? 'Стартирай верификация' : 'Верификация в процес'}
                            </Button>
                          )}
                        </>
                      ) : (
                        // Different buttons based on current user role and profile being viewed
                        <>
                          {currentUserRole === 'FREELANCER' || currentUserRole === 'AGENCY' ? (
                            <Button
                              variant="outlined"
                              fullWidth
                              disabled
                              sx={{
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'rgba(255, 255, 255, 0.5)',
                                py: { xs: 1.2, sm: 1.5 },
                                px: { xs: 2, sm: 3 },
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                fontWeight: 600,
                              }}
                            >
                              Само клиенти могат да се свързват
                            </Button>
                          ) : null}
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>

              {/* Quick Stats */}
              <Fade in={true} timeout={1000}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 4,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                      Статистики
                    </Typography>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          Отзиви
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                          {profileData.rating_cnt || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          Член от
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                          {new Date(profileData.created_at).getFullYear()}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Stack>
          </Grid>

          {/* Right Sidebar - Main Content */}
          <Grid item xs={12} md={8}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 4,
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden',
                  }}
                >
              {/* Tabs */}
              <Box sx={{ 
                borderBottom: 1, 
                borderColor: 'rgba(255, 255, 255, 0.1)',
                overflow: 'hidden'
              }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    '& .MuiTabs-scrollButtons': {
                      color: '#28A0F0',
                      '&.Mui-disabled': {
                        color: 'rgba(40, 160, 240, 0.3)',
                      },
                    },
                    '& .MuiTab-root': {
                      color: '#a0a0a0',
                      fontWeight: 600,
                      minHeight: { xs: 48, sm: 64 },
                      padding: { xs: '8px 12px', sm: '12px 16px' },
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      '&.Mui-selected': {
                        color: '#28A0F0',
                      },
                      '&:hover': {
                        color: '#ffffff',
                        backgroundColor: 'rgba(40, 160, 240, 0.1)',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#28A0F0',
                      height: 3,
                    },
                    '& .MuiTabs-flexContainer': {
                      gap: { xs: 0, sm: 1 },
                    },
                  }}
                >
                  <Tab 
                    label={t('profileEdit.tabs.overview')} 
                    icon={<PersonIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />} 
                    iconPosition="start" 
                  />
                  <Tab 
                    label={t('profileEdit.tabs.skills')} 
                    icon={<CodeIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />} 
                    iconPosition="start" 
                  />
                  <Tab 
                    label={t('profileEdit.tabs.portfolio')} 
                    icon={<AssignmentIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />} 
                    iconPosition="start" 
                  />
                  <Tab 
                    label={t('profileEdit.tabs.experience')} 
                    icon={<WorkIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />} 
                    iconPosition="start" 
                  />
                  <Tab 
                    label={t('profileEdit.tabs.education')} 
                    icon={<SchoolIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />} 
                    iconPosition="start" 
                  />
                  <Tab 
                    label={t('profileEdit.tabs.reviews')} 
                    icon={<StarIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />} 
                    iconPosition="start" 
                  />
                </Tabs>
              </Box>

              {/* Tab Panels */}
              <TabPanel value={activeTab} index={0}>
                {/* Overview Tab */}
                <Stack spacing={4}>
                  {/* Hero Section */}
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)',
                    p: 4,
                    borderRadius: 3,
                    border: '1px solid rgba(40, 160, 240, 0.2)',
                  }}>
                        <Typography variant="h3" sx={{ 
                          color: '#ffffff', 
                          fontWeight: 700, 
                          mb: 2,
                          background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}>
                          {profileData.company || getRoleText(profileData.role)}
                        </Typography>
                        <Typography variant="h5" sx={{ color: '#a0a0a0', mb: 3, fontWeight: 400 }}>
                          {profileData.skills?.slice(0, 5).join(' • ') || 'Професионални умения'}
                        </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MoneyIcon sx={{ color: '#28A0F0' }} />
                            <Typography variant="h4" sx={{ color: '#28A0F0', fontWeight: 700 }}>
                              €{profileData.hourly_rate || 0}
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#a0a0a0' }}>
                              /час
                            </Typography>
                          </Box>
                          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StarIcon sx={{ color: '#fbbf24' }} />
                            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                              {profileData.rating_avg || 0}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                              ({profileData.rating_cnt || 0} отзива)
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                  {/* Bio Section */}
              {profileData.bio && (
                  <Card
                    sx={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 3,
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                        За мен
                      </Typography>
                        {isEditMode && isOwnProfile ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={6}
                            value={editForm.bio}
                            onChange={(e) => handleInputChange('bio', e.target.value)}
                            placeholder={t('profileEdit.placeholders.bio')}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&:hover fieldset': { borderColor: '#28A0F0' },
                                '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                              },
                              '& .MuiInputLabel-root': { color: '#a0a0a0' },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                            }}
                          />
                        ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#d0d0d0',
                          lineHeight: 1.8,
                          fontSize: '1.1rem',
                        }}
                      >
                        {profileData.bio}
                      </Typography>
                        )}
                    </CardContent>
                  </Card>
                  )}
                </Stack>
              </TabPanel>

              {/* Skills Tab */}
              <TabPanel value={activeTab} index={1}>
                <Stack spacing={3}>
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Технически умения
                  </Typography>
                  
                  {isEditMode && isOwnProfile ? (
                    <Box>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={skillOptions}
                        value={editForm.skills}
                        onChange={(_, newValue) => {
                          setEditForm(prev => ({
                            ...prev,
                            skills: newValue
                          }));
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              label={option}
                              onDelete={() => handleSkillRemove(option)}
                    sx={{
                                backgroundColor: 'rgba(40, 160, 240, 0.2)',
                                color: '#28A0F0',
                                border: '1px solid rgba(40, 160, 240, 0.4)',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                '& .MuiChip-deleteIcon': {
                                  color: '#28A0F0',
                                  '&:hover': {
                                    color: '#ef4444',
                                  },
                                },
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={t('profileEdit.placeholders.addSkills')}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                color: '#ffffff',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#28A0F0',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#28A0F0',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: '#a0a0a0',
                              },
                              '& .MuiInputBase-input': {
                                color: '#ffffff',
                              },
                            }}
                          />
                        )}
                      />
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mt: 1 }}>
                        Можете да изберете от предложените умения или да добавите свои собствени
                      </Typography>
                    </Box>
                  ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {profileData.skills?.map((skill, index) => (
                          <Chip
                            key={index}
                            label={skill}
                            sx={{
                              backgroundColor: 'rgba(40, 160, 240, 0.15)',
                              color: '#28A0F0',
                              border: '1px solid rgba(40, 160, 240, 0.3)',
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              py: 2,
                              px: 1,
                              '&:hover': {
                                backgroundColor: 'rgba(40, 160, 240, 0.25)',
                                transform: 'translateY(-1px)',
                              },
                            }}
                          />
                        ))}
                      {(!profileData.skills || profileData.skills.length === 0) && (
                        <Typography variant="body2" sx={{ color: '#a0a0a0', fontStyle: 'italic' }}>
                          Няма добавени умения
                        </Typography>
                      )}
                      </Box>
              )}
            </Stack>
              </TabPanel>

              {/* Portfolio Tab */}
              <TabPanel value={activeTab} index={2}>
                <Stack spacing={{ xs: 2, sm: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 0 }
                  }}>
                    <Typography variant="h5" sx={{ 
                      color: '#ffffff', 
                      fontWeight: 600,
                      fontSize: { xs: '1.3rem', sm: '1.5rem' }
                    }}>
                      Портфолио
                    </Typography>
                    {isEditMode && isOwnProfile && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddPortfolio}
                        sx={{
                          background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                          color: '#ffffff',
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 2, sm: 3 },
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                          minWidth: { xs: 'auto', sm: 'auto' },
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        Добави проект
                      </Button>
                    )}
                  </Box>
                  
                  {portfolio.length > 0 ? (
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      {portfolio.map((item, index) => (
                        <Grid item xs={12} sm={6} lg={4} key={item.id}>
                          <Card
                            sx={{
                              background: 'rgba(40, 160, 240, 0.05)',
                              border: '1px solid rgba(40, 160, 240, 0.2)',
                              borderRadius: { xs: 2, sm: 3 },
                              overflow: 'hidden',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: { xs: 'none', sm: 'translateY(-4px)' },
                                boxShadow: { 
                                  xs: '0 4px 12px rgba(40, 160, 240, 0.1)', 
                                  sm: '0 8px 25px rgba(40, 160, 240, 0.2)' 
                                },
                              }
                            }}
                          >
                            {isEditMode && isOwnProfile ? (
                              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center', 
                                  mb: 2,
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  gap: { xs: 1, sm: 0 }
                                }}>
                                  <Typography variant="h6" sx={{ 
                                    color: '#ffffff',
                                    fontSize: { xs: '1rem', sm: '1.25rem' }
                                  }}>
                                    Проект #{index + 1}
                                  </Typography>
                                  <IconButton
                                    onClick={() => handleRemovePortfolio(item.id)}
                                    sx={{ 
                                      color: '#ef4444',
                                      alignSelf: { xs: 'flex-end', sm: 'auto' }
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                
                                <Stack spacing={{ xs: 1.5, sm: 2 }}>
                                  <TextField
                                    fullWidth
                                    label={t('profileEdit.portfolio.projectTitle')}
                                    value={item.title}
                                    onChange={(e) => handleUpdatePortfolio(item.id, 'title', e.target.value)}
                                    size="small"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                        '&:hover fieldset': { borderColor: '#28A0F0' },
                                        '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                                      },
                                      '& .MuiInputLabel-root': { 
                                        color: '#a0a0a0',
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                      },
                                      '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                                      '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                      },
                                    }}
                                  />
                                  
                                  <TextField
                                    fullWidth
                                    label={t('profileEdit.portfolio.description')}
                                    multiline
                                    rows={3}
                                    value={item.description}
                                    onChange={(e) => handleUpdatePortfolio(item.id, 'description', e.target.value)}
                                    size="small"
              sx={{ 
                                      '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                        '&:hover fieldset': { borderColor: '#28A0F0' },
                                        '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                                      },
                                      '& .MuiInputLabel-root': { color: '#a0a0a0' },
                                      '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                                    }}
                                  />
                                  
                                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        fullWidth
                                        label={t('profileEdit.portfolio.projectUrl')}
                                        value={item.project_url}
                                        onChange={(e) => handleUpdatePortfolio(item.id, 'project_url', e.target.value)}
                                        size="small"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            color: '#ffffff',
                                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                            '&:hover fieldset': { borderColor: '#28A0F0' },
                                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                                          },
                                          '& .MuiInputLabel-root': { 
                                            color: '#a0a0a0',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                          },
                                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                                          '& .MuiInputBase-input': {
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                          },
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        fullWidth
                                        label="GitHub URL"
                                        value={item.github_url}
                                        onChange={(e) => handleUpdatePortfolio(item.id, 'github_url', e.target.value)}
                                        size="small"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            color: '#ffffff',
                                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                            '&:hover fieldset': { borderColor: '#28A0F0' },
                                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                                          },
                                          '& .MuiInputLabel-root': { 
                                            color: '#a0a0a0',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                          },
                                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                                          '& .MuiInputBase-input': {
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                          },
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                  
                                  <TextField
                                    fullWidth
                                    label={t('profileEdit.portfolio.completionDate')}
                                    type="date"
                                    value={item.completed_date}
                                    onChange={(e) => handleUpdatePortfolio(item.id, 'completed_date', e.target.value)}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: '#ffffff',
                                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                        '&:hover fieldset': { borderColor: '#28A0F0' },
                                        '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                                      },
                                      '& .MuiInputLabel-root': { color: '#a0a0a0' },
                                      '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                                    }}
                                  />
                                  
                                  <Autocomplete
                                    multiple
                                    freeSolo
                                    options={technologyOptions}
                                    value={item.technologies}
                                    onChange={(_, newValue) => {
                                      handleUpdatePortfolio(item.id, 'technologies', newValue);
                                    }}
                                    renderTags={(value, getTagProps) =>
                                      value.map((option, index) => (
                                        <Chip
                                          {...getTagProps({ index })}
                                          key={option}
                                          label={option}
                                          size="small"
                                          sx={{
                                            backgroundColor: 'rgba(40, 160, 240, 0.2)',
                                            color: '#28A0F0',
                                            border: '1px solid rgba(40, 160, 240, 0.4)',
                                            fontSize: '0.8rem',
                                          }}
                                        />
                                      ))
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={t('profileEdit.portfolio.technologies')}
                                        size="small"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            color: '#ffffff',
                                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                            '&:hover fieldset': { borderColor: '#28A0F0' },
                                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                                          },
                                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                                        }}
                                      />
                                    )}
                                  />
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={item.featured}
                                          onChange={(e) => handleUpdatePortfolio(item.id, 'featured', e.target.checked)}
                                          sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                              color: '#28A0F0',
                                              '& + .MuiSwitch-track': {
                                                backgroundColor: '#28A0F0',
                                              },
                                            },
                                          }}
                                        />
                                      }
                                      label={t('profileEdit.portfolio.featured')}
                                      sx={{ color: '#ffffff' }}
                                    />
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                                      id={`portfolio-image-${item.id}`}
                  type="file"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          handlePortfolioImageUpload(item.id, file);
                                        }
                                      }}
                                    />
                                    <label htmlFor={`portfolio-image-${item.id}`}>
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<PhotoCameraIcon />}
                                        size="small"
                    sx={{
                                          borderColor: 'rgba(40, 160, 240, 0.5)',
                      color: '#28A0F0',
                      '&:hover': {
                                            borderColor: '#28A0F0',
                        backgroundColor: 'rgba(40, 160, 240, 0.1)',
                      },
                    }}
                  >
                                        {item.image_url ? 'Смени изображение' : 'Добави изображение'}
                  </Button>
                </label>
                                    {item.image_url && (
                                      <Box sx={{ width: 60, height: 40, borderRadius: 1, overflow: 'hidden' }}>
                                        <img
                                          src={item.image_url}
                                          alt="Portfolio preview"
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </Stack>
                              </Box>
                            ) : (
                              <Box>
                                {item.image_url && (
                                  <Box sx={{ 
                                    height: { xs: 150, sm: 200 }, 
                                    overflow: 'hidden' 
                                  }}>
                                    <img
                                      src={item.image_url}
                                      alt={item.title}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  </Box>
                                )}
                                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'flex-start', 
                                    mb: 2,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 1, sm: 0 }
                                  }}>
                                    <Typography variant="h6" sx={{ 
                                      color: '#ffffff', 
                                      fontWeight: 600,
                                      fontSize: { xs: '1rem', sm: '1.25rem' },
                                      wordBreak: 'break-word'
                                    }}>
                                      {item.title}
                                    </Typography>
                                    {item.featured && (
                                      <Chip
                                        label={t('profileEdit.portfolio.featuredLabel')}
                                        size="small"
                                        sx={{
                                          backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                          color: '#10b981',
                                          border: '1px solid #10b981',
                                          fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                          height: { xs: 20, sm: 24 },
                                          alignSelf: { xs: 'flex-start', sm: 'auto' }
                                        }}
                                      />
                                    )}
                                  </Box>
                                  
                                  <Typography variant="body2" sx={{ 
                                    color: '#d0d0d0', 
                                    mb: 2, 
                                    lineHeight: 1.6,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                  }}>
                                    {item.description}
                                  </Typography>
                                  
                                  {item.technologies.length > 0 && (
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexWrap: 'wrap', 
                                      gap: { xs: 0.5, sm: 0.5 }, 
                                      mb: 2 
                                    }}>
                                      {item.technologies.map((tech, techIndex) => (
                                        <Chip
                                          key={techIndex}
                                          label={tech}
                                          size="small"
                                          sx={{
                                            backgroundColor: 'rgba(40, 160, 240, 0.15)',
                                            color: '#28A0F0',
                                            border: '1px solid rgba(40, 160, 240, 0.3)',
                                            fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                            height: { xs: 20, sm: 24 },
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                  
                                  <Box sx={{ 
                                    display: 'flex', 
                                    gap: { xs: 1, sm: 2 }, 
                                    alignItems: 'center',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    flexWrap: 'wrap'
                                  }}>
                                    {item.project_url && (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<LinkIcon />}
                                        href={item.project_url}
                                        target="_blank"
                                        sx={{
                                          borderColor: 'rgba(40, 160, 240, 0.5)',
                                          color: '#28A0F0',
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                          py: { xs: 0.5, sm: 1 },
                                          px: { xs: 1.5, sm: 2 },
                                          minWidth: { xs: 'auto', sm: 'auto' },
                                          width: { xs: '100%', sm: 'auto' },
                                          '&:hover': {
                                            borderColor: '#28A0F0',
                                            backgroundColor: 'rgba(40, 160, 240, 0.1)',
                                          },
                                        }}
                                      >
                                        Проект
                                      </Button>
                                    )}
                                    {item.github_url && (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<GitHubIcon />}
                                        href={item.github_url}
                                        target="_blank"
                                        sx={{
                                          borderColor: 'rgba(255, 255, 255, 0.3)',
                                          color: '#ffffff',
                                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                          py: { xs: 0.5, sm: 1 },
                                          px: { xs: 1.5, sm: 2 },
                                          minWidth: { xs: 'auto', sm: 'auto' },
                                          width: { xs: '100%', sm: 'auto' },
                                          '&:hover': {
                                            borderColor: '#ffffff',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                          },
                                        }}
                                      >
                                        GitHub
                                      </Button>
                                    )}
                                    {item.completed_date && (
                                      <Typography variant="body2" sx={{ 
                                        color: '#a0a0a0', 
                                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                        textAlign: { xs: 'center', sm: 'left' },
                                        width: { xs: '100%', sm: 'auto' }
                                      }}>
                                        {new Date(item.completed_date).toLocaleDateString('bg-BG')}
                                      </Typography>
                                    )}
                                  </Box>
            </Box>
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ 
                      background: 'rgba(40, 160, 240, 0.1)', 
                      border: '2px dashed rgba(40, 160, 240, 0.3)',
                      borderRadius: { xs: 2, sm: 3 },
                      p: { xs: 3, sm: 4 },
                      textAlign: 'center'
                    }}>
                      <AssignmentIcon sx={{ 
                        fontSize: { xs: 40, sm: 60 }, 
                        color: '#28A0F0', 
                        mb: 2 
                      }} />
                      <Typography variant="h6" sx={{ 
                        color: '#ffffff', 
                        mb: 1,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                      }}>
                        Няма проекти в портфолиото
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: '#a0a0a0',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                      }}>
                        {isEditMode ? 'Добавете проекти, за да покажете вашите умения' : 'Портфолиото е празно'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </TabPanel>

              {/* Experience Tab */}
              <TabPanel value={activeTab} index={3}>
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                      Професионален опит
              </Typography>
                    {isEditMode && isOwnProfile && (
                  <Button
                    variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddExperience}
                    sx={{
                          borderColor: 'rgba(40, 160, 240, 0.5)',
                      color: '#28A0F0',
                      '&:hover': {
                            borderColor: '#28A0F0',
                        backgroundColor: 'rgba(40, 160, 240, 0.1)',
                      },
                    }}
                  >
                        Добави опит
                  </Button>
            )}
          </Box>
          
                  {experience.length > 0 ? (
                    <Stack spacing={2}>
                      {experience.map((exp, index) => (
                        <Card
                          key={exp.id}
                    sx={{
                            background: 'rgba(40, 160, 240, 0.05)',
                            border: '1px solid rgba(40, 160, 240, 0.2)',
                            borderRadius: 2,
                            p: 3,
                          }}
                        >
                          <Stack spacing={2}>
                            {isEditMode && isOwnProfile ? (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                                    Работен опит #{index + 1}
              </Typography>
                                  <IconButton
                                    onClick={() => handleRemoveExperience(exp.id)}
                                    sx={{ color: '#ef4444' }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
          </Box>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                                      label={t('profileEdit.experience.company')}
                                      value={exp.company}
                                      onChange={(e) => handleUpdateExperience(exp.id, 'company', e.target.value)}
                                      size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                  <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                                      label={t('profileEdit.experience.position')}
                                      value={exp.position}
                                      onChange={(e) => handleUpdateExperience(exp.id, 'position', e.target.value)}
                                      size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                  <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                                      label={t('profileEdit.experience.startDate')}
                                      type="date"
                                      value={exp.startDate}
                                      onChange={(e) => handleUpdateExperience(exp.id, 'startDate', e.target.value)}
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                  <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                                      label={t('profileEdit.experience.endDate')}
                                      type="date"
                                      value={exp.endDate}
                                      onChange={(e) => handleUpdateExperience(exp.id, 'endDate', e.target.value)}
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                                      disabled={exp.current}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                  <Grid item xs={12}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={exp.current}
                                          onChange={(e) => handleUpdateExperience(exp.id, 'current', e.target.checked)}
                sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                              color: '#28A0F0',
                                              '& + .MuiSwitch-track': {
                                                backgroundColor: '#28A0F0',
                                              },
                                            },
                                          }}
                                        />
                                      }
                                      label={t('profileEdit.experience.currentWork')}
                                      sx={{ color: '#ffffff' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                                      label={t('profileEdit.experience.description')}
                multiline
                                      rows={3}
                                      value={exp.description}
                                      onChange={(e) => handleUpdateExperience(exp.id, 'description', e.target.value)}
                                      size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                </Grid>
                              </>
                            ) : (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                      {exp.position}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#28A0F0', fontWeight: 500 }}>
                                      {exp.company}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                      {exp.startDate} - {exp.current ? 'Настояще' : exp.endDate}
                                    </Typography>
                                  </Box>
                                </Box>
                                {exp.description && (
                                  <Typography variant="body2" sx={{ color: '#d0d0d0', lineHeight: 1.6 }}>
                                    {exp.description}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Stack>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ 
                      background: 'rgba(40, 160, 240, 0.1)', 
                      border: '2px dashed rgba(40, 160, 240, 0.3)',
                      borderRadius: 3,
                      p: 4,
                      textAlign: 'center'
                    }}>
                      <WorkIcon sx={{ fontSize: 60, color: '#28A0F0', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                        Няма записи за работен опит
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                        {isEditMode ? 'Добавете информация за вашия професионален опит' : 'Опитът не е посочен'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </TabPanel>

              {/* Education Tab */}
              <TabPanel value={activeTab} index={4}>
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                      Образование
                    </Typography>
                    {isEditMode && isOwnProfile && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddEducation}
                        sx={{
                          borderColor: 'rgba(40, 160, 240, 0.5)',
                          color: '#28A0F0',
                          '&:hover': {
                            borderColor: '#28A0F0',
                            backgroundColor: 'rgba(40, 160, 240, 0.1)',
                          },
                        }}
                      >
                        Добави образование
                      </Button>
                    )}
                  </Box>
                  
                  {education.length > 0 ? (
                    <Stack spacing={2}>
                      {education.map((edu, index) => (
                        <Card
                          key={edu.id}
                          sx={{
                            background: 'rgba(40, 160, 240, 0.05)',
                            border: '1px solid rgba(40, 160, 240, 0.2)',
                            borderRadius: 2,
                            p: 3,
                          }}
                        >
                          <Stack spacing={2}>
                            {isEditMode && isOwnProfile ? (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                                    Образование #{index + 1}
                                  </Typography>
                                  <IconButton
                                    onClick={() => handleRemoveEducation(edu.id)}
                                    sx={{ color: '#ef4444' }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                                      label={t('profileEdit.education.institution')}
                                      value={edu.institution}
                                      onChange={(e) => handleUpdateEducation(edu.id, 'institution', e.target.value)}
                                      size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                  <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                                      label={t('profileEdit.education.degree')}
                                      value={edu.degree}
                                      onChange={(e) => handleUpdateEducation(edu.id, 'degree', e.target.value)}
                                      size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                  <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                                      label={t('profileEdit.education.field')}
                                      value={edu.field}
                                      onChange={(e) => handleUpdateEducation(edu.id, 'field', e.target.value)}
                                      size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                  <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                                      label={t('profileEdit.education.startDate')}
                                      type="date"
                                      value={edu.startDate}
                                      onChange={(e) => handleUpdateEducation(edu.id, 'startDate', e.target.value)}
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      label={t('profileEdit.education.endDate')}
                                      type="date"
                                      value={edu.endDate}
                                      onChange={(e) => handleUpdateEducation(edu.id, 'endDate', e.target.value)}
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                    sx={{
                                        '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                          '&:hover fieldset': { borderColor: '#28A0F0' },
                                          '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                                        },
                                        '& .MuiInputLabel-root': { color: '#a0a0a0' },
                                        '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
              <TextField
                fullWidth
                                      label={t('profileEdit.education.description')}
                                      multiline
                                      rows={3}
                                      value={edu.description}
                                      onChange={(e) => handleUpdateEducation(edu.id, 'description', e.target.value)}
                                      size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Grid>
          </Grid>
                              </>
                            ) : (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box>
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                      {edu.degree}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#28A0F0', fontWeight: 500 }}>
                                      {edu.institution}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                      {edu.field}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                      {edu.startDate} - {edu.endDate}
                                    </Typography>
                                  </Box>
                                </Box>
                                {edu.description && (
                                  <Typography variant="body2" sx={{ color: '#d0d0d0', lineHeight: 1.6 }}>
                                    {edu.description}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Stack>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ 
                      background: 'rgba(40, 160, 240, 0.1)', 
                      border: '2px dashed rgba(40, 160, 240, 0.3)',
                      borderRadius: 3,
                      p: 4,
                      textAlign: 'center'
                    }}>
                      <SchoolIcon sx={{ fontSize: 60, color: '#28A0F0', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                        Няма записи за образование
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                        {isEditMode ? 'Добавете информация за вашето образование' : 'Образованието не е посочено'}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </TabPanel>

              {/* Reviews Tab */}
              <TabPanel value={activeTab} index={5}>
                <Stack spacing={3}>
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Отзиви
                  </Typography>
                  
                  {reviews && reviews.length > 0 ? (
                    <Stack spacing={2}>
                      {reviews.map((review, index) => (
                        <Card
                    key={index}
                    sx={{
                            background: 'rgba(40, 160, 240, 0.05)',
                            border: '1px solid rgba(40, 160, 240, 0.2)',
                            borderRadius: 2,
                            p: 3,
                          }}
                        >
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                  src={review.client_avatar || undefined}
                                  sx={{ width: 40, height: 40 }}
                                />
                                <Box>
                                  <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                    {review.client_name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                    {new Date(review.created_at).toLocaleDateString('bg-BG')}
                                  </Typography>
              </Box>
                              </Box>
                              <Rating
                                value={review.rating}
                                readOnly
                                size="small"
            sx={{
                                  '& .MuiRating-iconFilled': { color: '#fbbf24' },
                                  '& .MuiRating-iconEmpty': { color: '#374151' },
                                }}
                              />
                            </Box>
                            <Typography variant="body2" sx={{ color: '#d0d0d0', lineHeight: 1.6 }}>
                              {review.comment}
                            </Typography>
                            {review.project_title && (
                              <Typography variant="body2" sx={{ color: '#28A0F0', fontStyle: 'italic' }}>
                                Проект: {review.project_title}
                              </Typography>
                            )}
                          </Stack>
                  </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ 
                      background: 'rgba(40, 160, 240, 0.1)', 
                      border: '2px dashed rgba(40, 160, 240, 0.3)',
                      borderRadius: 3,
                      p: 4,
                      textAlign: 'center'
                    }}>
                      <StarIcon sx={{ fontSize: 60, color: '#28A0F0', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                        Няма отзиви
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                        Все още няма получени отзиви от клиенти
                      </Typography>
                    </Box>
              )}
            </Stack>
              </TabPanel>
            </Card>
            </Grid>
          </Grid>
      </Container>

      <Footer />

      {/* Error Alert */}
      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#ffffff' }}
          >
            {error}
          </Alert>
        </Snackbar>
      )}

      {/* Success Message */}
      {successMessage && (
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity="success" 
            onClose={() => setSuccessMessage(null)}
            sx={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#ffffff' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      )}

      {/* Floating Action Button for Edit Mode */}
      {isOwnProfile && !isEditMode && (
        <Zoom in={true}>
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 },
              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                transform: 'scale(1.1)',
              },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.2rem', sm: '1.5rem' },
              },
            }}
            onClick={handleEditModeToggle}
          >
            <EditIcon />
          </Fab>
        </Zoom>
      )}
    </Box>
  );
};

export default ProfilePage;
